import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, ArrowLeft, Loader2, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_ppf-auto-manager/artifacts/h7byux7s_UP-Logo-White.png";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }

    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name}!`);
      
      if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/partner');
      }
    } catch (error) {
      console.error('Login error:', error);
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-6">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-red-600/5 rounded-full blur-[100px] pointer-events-none" />

      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative w-full max-w-md"
      >
        <Link to="/" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-8 group">
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform duration-200" />
          <span>Back to Home</span>
        </Link>

        <div className="bg-zinc-900/60 backdrop-blur-xl border border-white/10 rounded-xl p-8">
          <div className="text-center mb-8">
            <img src={LOGO_URL} alt="Unique Protection" className="h-12 mx-auto mb-4" />
            <h1 className="font-['Chivo'] text-2xl font-bold text-white mb-2">Welcome Back</h1>
            <p className="text-sm text-zinc-400">Sign in to access your dashboard</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-300">Email</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  id="email" type="email" data-testid="login-email-input"
                  placeholder="your@email.com" value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-11 bg-zinc-950/50 border-white/10 focus:border-red-500/50 text-white placeholder:text-zinc-600 h-12"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-300">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-500" />
                <Input
                  id="password" type={showPassword ? "text" : "password"} data-testid="login-password-input"
                  placeholder="••••••••" value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-11 pr-11 bg-zinc-950/50 border-white/10 focus:border-red-500/50 text-white placeholder:text-zinc-600 h-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 transition-colors"
                  tabIndex={-1}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button type="submit" data-testid="login-submit-btn" disabled={loading}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-bold h-12 shadow-[0_0_20px_rgba(220,38,38,0.3)] hover:shadow-[0_0_30px_rgba(220,38,38,0.5)]">
              {loading ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Signing in...</> : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 p-4 bg-zinc-950/50 rounded-lg border border-white/5">
            <p className="text-sm text-zinc-400 text-center mb-2">Interested in becoming a partner?</p>
            <a 
              href="https://www.uniqueprotection.eu" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-sm text-red-400 hover:text-red-300 text-center block font-medium"
            >
              Contact us at uniqueprotection.eu
            </a>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
